import type { WorkflowOverlay, WorkflowOverlayContext } from "../types";

function normalizedTerms(value: string | null | undefined): string[] {
  if (!value) return [];
  return value
    .split(/[,\n]/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function uniqueCaseInsensitive(values: string[]): string[] {
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const value of values) {
    const normalized = value.trim().toLowerCase();
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    unique.push(value.trim());
  }
  return unique;
}

export function brandWorkflowOverlaySignature(
  context: WorkflowOverlayContext,
): string | null {
  const brand = context.brand;
  if (!brand) return null;

  return JSON.stringify({
    id: brand.id,
    targetAudience: brand.targetAudience,
    strategyAudience: brand.strategyAudienceWho,
    strategyArtifactStatus: brand.strategyArtifactStatus,
    voiceArtifactStatus: brand.voiceArtifactStatus,
  });
}

export function brandWorkflowOverlay(
  context: WorkflowOverlayContext,
): WorkflowOverlay | null {
  const brand = context.brand;
  if (!brand) return null;

  const preferredAudienceTerms = normalizedTerms(brand.targetAudience);
  const strategyAudience = brand.strategyAudienceWho?.trim();
  const preferredLanguage = uniqueCaseInsensitive(
    strategyAudience ? [strategyAudience, ...preferredAudienceTerms] : preferredAudienceTerms,
  );

  const requiredProofArtifacts: string[] = [];
  if (brand.strategyArtifactStatus === "healthy") {
    requiredProofArtifacts.push("Anchor the draft in the confirmed strategy artifact before making broad market claims.");
  }
  if (brand.voiceArtifactStatus === "healthy") {
    requiredProofArtifacts.push("Keep phrasing aligned with the confirmed voice artifact before final approval.");
  }

  const overlaySteps: NonNullable<WorkflowOverlay["steps"]> = {};

  if (preferredLanguage.length > 0) {
    overlaySteps.language_clarity_review = {
      paramOverrides: {
        allowed_terms: preferredLanguage,
      },
      notes: [
        `Preferred audience language: ${preferredLanguage.join(" · ")}`,
      ],
    };
  }

  if (requiredProofArtifacts.length > 0) {
    overlaySteps.content_validation = {
      notes: requiredProofArtifacts,
    };
    overlaySteps.approval_gate = {
      notes: requiredProofArtifacts,
    };
  }

  return Object.keys(overlaySteps).length > 0
    ? {
        id: `brand:${brand.id}`,
        steps: overlaySteps,
      }
    : null;
}
